import React from "react";
import PropTypes from "prop-types";

import {
  ApplyChangeAtOptions,
  CouponForm,
  CouponInformation,
  DeltaLabel,
  formatDate,
  Header,
  Money,
  pluralize,
  StaticBulletList,
  SummaryLayout,
  SummaryPlaceholder,
  SummaryRow,
  UnitPrice,
} from "./components";

function SummaryStep({
  loading,
  error,
  currency,
  billingPeriod,
  currentPlan,
  updatedPlan,
  updatedPlanFeatures,
  coupon,
  allowApplyAtRenewal,
  onApplyAtRenewal,
}) {
  if (loading) return <SummaryPlaceholder />;

  if (error)
    return (
      <div data-test="summary-estimate-error" style={{ color: "red" }}>
        {error}
      </div>
    );

  return (
    <SummaryLayout
      nextBillingDate={formatDate("simpleDate")(updatedPlan.nextBillingDate)}
    >
      <Header as="h3">Current Subscription</Header>
      <SummaryRow
        text={currentPlan.name}
        subText={`${
          currentPlan.planMultiple > 1
            ? `${currentPlan.usedSeats} / ${currentPlan.numSeats}`
            : currentPlan.numSeats
        } ${pluralize("seat", currentPlan.usedSeats)}`}
        unitPrice={<UnitPrice {...currentPlan} currency={currency} />}
        totalPrice={
          <div>
            <Money currency={currency}>{currentPlan.nextBillingPrice}</Money>
            {billingPeriod && ` (billed ${billingPeriod})`}
          </div>
        }
      />
      <Header as="h3">Updated Subscription</Header>
      <SummaryRow
        text={updatedPlan.name}
        subText={`${updatedPlan.usedSeats}${
          updatedPlan.planMultiple > 1 ? ` / ${updatedPlan.numSeats}` : ""
        } ${pluralize("seat", updatedPlan.usedSeats)}`}
        unitPrice={<UnitPrice {...updatedPlan} currency={currency} />}
        totalPrice={
          <div>
            <div>
              <Money currency={currency}>{currentPlan.nextBillingPrice}</Money>
              {billingPeriod && ` (billed ${billingPeriod})`}
            </div>
            <DeltaLabel deltaQty={updatedPlan.deltaPrice}>
              <Money currency={currency}>{updatedPlan.deltaPrice}</Money>
            </DeltaLabel>
          </div>
        }
      >
        <StaticBulletList items={updatedPlanFeatures} />
      </SummaryRow>
      <SummaryRow
        text="Total"
        totalPrice={
          <div>
            <Money currency={currency}>{updatedPlan.nextBillingPrice}</Money>
            {billingPeriod && ` (billed ${billingPeriod})`}
          </div>
        }
      />
      <ApplyChangeAtOptions
        allowApplyAtRenewal={allowApplyAtRenewal}
        onChange={onApplyAtRenewal}
      />
      {coupon.id ? (
        <CouponInformation currency={currency} {...coupon} />
      ) : (
        <CouponForm
          loading={coupon.validating}
          error={coupon.error}
          onApply={coupon.onCouponApplied}
        />
      )}
    </SummaryLayout>
  );
}

SummaryStep.propTypes = {
  billingPeriod: PropTypes.string,
  currency: PropTypes.string,
  currentPlan: PropTypes.object,
  updatedPlan: PropTypes.object,
  updatedPlanFeatures: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  error: PropTypes.string,
  coupon: PropTypes.object.isRequired,
  onApplyAtRenewal: PropTypes.func,
  allowApplyAtRenewal: PropTypes.bool,
};

SummaryStep.defaultProps = {
  currentPlan: {},
  updatedPlan: {},
  updatedPlanFeatures: [],
  loading: false,
  error: null,
  billingPeriod: "",
  currency: "USD",
  onApplyAtRenewal: () => {},
  allowApplyAtRenewal: false,
};

export default SummaryStep;
